import type { FieldDescriptor } from './FormScanner.js';

export function applyValues(
  mapping: Record<string, string>,
  form: HTMLFormElement,
  fieldsMetadata?: FieldDescriptor[]
): void {
  Object.entries(mapping).forEach(([name, value]) => {
    console.log(`[AI Copilot] Trying to fill field "${name}" with value: ${value}`);
    
    const control = form.elements.namedItem(name) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
      | null;
    if (!control) return;
    
    // Проверяем data-ai-private атрибут
    if (control instanceof HTMLElement && control.dataset.aiPrivate === 'true') {
      console.log(`[AI Copilot] Skipping field "${name}" - marked as data-ai-private`);
      return;
    }
    
    // Проверяем isPrivate в метаданных поля
    if (fieldsMetadata) {
      const fieldMeta = fieldsMetadata.find(f => f.name === name);
      if (fieldMeta?.isPrivate) {
        console.log(`[AI Copilot] Skipping field "${name}" - detected as sensitive by field scanner`);
        return;
      }
    }
    
    // Дополнительная защита: базовая проверка чувствительных полей
    if (control instanceof HTMLInputElement && control.type === 'password') {
      console.log(`[AI Copilot] Skipping field "${name}" - password field`);
      return;
    }

    // Для радиокнопок нужно найти ту, у которой value совпадает
    if (control instanceof HTMLInputElement && control.type === 'radio') {
      const radioToSelect = Array.from(form.elements)
        .filter(el => el instanceof HTMLInputElement && el.type === 'radio' && el.name === name && el.value === value)
        .pop() as HTMLInputElement | undefined;
      if (radioToSelect) {
        radioToSelect.checked = true;
      } else {
        // Если значение не найдено, не меняем (или можно сбросить, если нужно)
        console.warn(`Radio option with name "${name}" and value "${value}" not found.`);
      }
    } else if (control instanceof HTMLInputElement && control.type === 'checkbox') {
      // Для одиночного чекбокса, если value - строка, интерпретируем ее как состояние "checked"
      // Обычно, если LLM возвращает имя чекбокса, значит его надо отметить.
      // Если значение пустое или его value атрибут, то ставим checked.
      // Более сложная логика может понадобиться если LLM возвращает true/false.
      if (typeof value === 'string') { // LLM может вернуть value чекбокса или просто его имя
         control.checked = true; // Просто отмечаем, если он есть в autofill
      }
    } else if (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement || control instanceof HTMLSelectElement) {
      control.value = value; // Для text, textarea, select
    }

    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

// Новая глобальная функция, адаптированная для работы с checkboxgroup (массив значений)
export const applyValuesGlobal = (values: { [key: string]: string | number | boolean | string[] | undefined }, fieldsMeta: FieldDescriptor[] = []) => {
  const forms = Array.from(document.forms);
  Object.entries(values).forEach(([name, value]) => {
    for (const form of forms) {
      const element = form.elements.namedItem(name);
      if (!element) continue;

      // Пропускаем приватные поля
      if (element instanceof HTMLElement && element.dataset.aiPrivate === 'true') break;
      const fieldMeta = fieldsMeta.find(f => f.name === name && f.formIndex === forms.indexOf(form));
      if (fieldMeta?.isPrivate) break;

      if (element instanceof RadioNodeList) {
        let radioApplied = false;
        Array.from(element).forEach(radioElement => {
          if (radioElement instanceof HTMLInputElement && radioElement.type === 'radio') {
            if (Array.isArray(value)) { // Для checkboxgroup, которые могут быть представлены RadioNodeList если имена совпадают
              radioElement.checked = value.includes(radioElement.value);
            } else if (typeof value === 'string') {
              radioElement.checked = radioElement.value === value;
            }
            if (radioElement.checked) {
              radioApplied = true;
              const event = new Event('input', { bubbles: true });
              radioElement.dispatchEvent(event);
              const changeEvent = new Event('change', { bubbles: true });
              radioElement.dispatchEvent(changeEvent);
            }
          }
        });
        if (radioApplied) break; // Применили к одной группе радиокнопок, выходим
      } else if (element instanceof HTMLInputElement && element.type === 'checkbox') {
        if (typeof value === 'boolean') {
          element.checked = value;
        } else if (typeof value === 'string') {
          // Если значение - строка, и это значение чекбокса, отмечаем
          // Или если это 'on'/'off' (не очень надежно, но часто встречается)
          element.checked = element.value === value || (value === 'on' && element.value === 'on');
        } else if (Array.isArray(value)) { // Для checkboxgroup
          element.checked = value.includes(element.value);
        }
        const event = new Event('input', { bubbles: true });
        element.dispatchEvent(event);
        const changeEvent = new Event('change', { bubbles: true });
        element.dispatchEvent(changeEvent);
        break; // Применили к чекбоксу, выходим
      } else if (element instanceof HTMLElement) {
        // Для select, textarea, input[type=text], etc.
        if (typeof value === 'string' || typeof value === 'number') {
          (element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value = String(value);
          const event = new Event('input', { bubbles: true });
          element.dispatchEvent(event);
          const changeEvent = new Event('change', { bubbles: true });
          element.dispatchEvent(changeEvent);
          break; // Применили значение, выходим
        }
      }
    }
  });
}; 